import React from 'react';
import { CreditsChip, CreditsPopover, LOW_BALANCE_THRESHOLD } from '../../components/shell';
import { OrgCredits } from '../../client/creditsClient';
import { REQUEST_PHASES, fixedRequestState } from './creditRequestFixtures';
import Section from './Section';
import DiscoveryCreditsGallery from './DiscoveryCreditsGallery';

/**
 * /design — sidebar credits chip states, and the popover's "Request more credits" (idle, pending, just requested, declined, could not send). Fixture data only (the live chip
 * renders `GET /orgs/:orgId/credits`); rendered on a navy swatch because the
 * chip lives in the nav rail, above the user card.
 */
const fixture = (balance: number, transactions: OrgCredits['transactions'] = []): OrgCredits => ({
  orgId: 'org-design',
  balance,
  lifetimeGranted: 5000,
  lifetimeSpent: 5000 - balance,
  transactions,
  pagination: { page: 1, limit: 8, total: transactions.length },
});

const ledger: OrgCredits['transactions'] = [
  { id: 't1', type: 'DEBIT', source: 'TASK', amount: 340, balanceAfter: 4660, useCase: 'SUPPLIER_RESEARCH', createdAt: '2026-07-28T09:12:00Z' },
  { id: 't2', type: 'DEBIT', source: 'TASK', amount: 5, balanceAfter: 4655, useCase: 'OUTREACH_MESSAGE', createdAt: '2026-07-27T16:40:00Z' },
  { id: 't3', type: 'REFUND', source: 'RECONCILIATION', amount: 340, balanceAfter: 4995, useCase: 'SUPPLIER_RESEARCH', createdAt: '2026-07-26T11:05:00Z' },
  { id: 't4', type: 'GRANT', source: 'ADMIN', amount: 5000, balanceAfter: 5000, createdAt: '2026-07-20T08:00:00Z' },
];

const Rail: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="w-[212px]">
    <p className="mb-s2 text-meta text-ink-muted">{label}</p>
    <div className="rounded-card bg-chrome px-s5 py-s5">{children}</div>
  </div>
);

const CreditsGallery: React.FC = () => (
  <>
    <Section title="Credits chip (sidebar footer)">
      <div className="flex flex-wrap items-start gap-s6">
        <Rail label="Normal">
          <CreditsChip credits={fixture(4935, ledger)} />
        </Rail>
        <Rail label={`Low (below ${LOW_BALANCE_THRESHOLD} — cheapest metered action)`}>
          <CreditsChip credits={fixture(3, ledger)} />
        </Rail>
        <Rail label="Zero">
          <CreditsChip credits={fixture(0, ledger)} />
        </Rail>
        <Rail label="API unreachable">
          <CreditsChip credits={null} unreachable />
        </Rail>
        <Rail label="Loading (first fetch)">
          <CreditsChip credits={null} loading />
        </Rail>
      </div>
      <div className="mt-s6">
        <p className="mb-s3 text-row-title text-ink">Popover — recent ledger</p>
        <div className="flex flex-wrap gap-s6">
          <CreditsPopover credits={fixture(4935, ledger)} />
          <CreditsPopover credits={fixture(5000)} />
        </div>
      </div>
      <div className="mt-s6">
        <p className="mb-s3 text-row-title text-ink">Popover — Request more credits</p>
        <div className="flex flex-wrap items-start gap-s6">
          {REQUEST_PHASES.map(({ label, phase }) => (
            <div key={phase}>
              <p className="mb-s2 text-meta text-ink-muted">{label}</p>
              <CreditsPopover credits={fixture(3, ledger)} creditRequest={fixedRequestState(phase)} />
            </div>
          ))}
        </div>
      </div>
    </Section>
    <DiscoveryCreditsGallery />
  </>
);

export default CreditsGallery;
